import { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { ProjectDialog } from "@/components/ProjectPhotos";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero } from "@/components/Reveal";
import { PROJECTS, IMG } from "@/lib/content";
import { CATEGORY_HI } from "@/lib/content_hi";
import { useLang } from "@/context/LanguageContext";

const FILTERS = ["All", "AI", "Robotics", "UAV", "Aerospace", "Events"];

export default function ProjectsGallery() {
  const { L } = useLang();
  const [f, setF] = useState("All");
  const [student, setStudent] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/showcase").then((r) => setStudent(r.data.items.map((x) => ({
      ...x, category: x.program, image: photoUrl(x.photos[0]), school: `${x.student_first_name} · ${x.school_name}`, student: true,
    })))).catch(() => {});
  }, []);
  const all = [...student, ...PROJECTS];
  const list = f === "All" ? all : all.filter((p) => p.category === f);

  return (
    <div data-testid="projects-page">
      <PageHero chip={L("Projects & Gallery", "प्रोजेक्ट्स और गैलरी")} title={L("Made by students. Proven in the real world.", "छात्रों द्वारा निर्मित। असली दुनिया में परखा हुआ।")} subtitle={L("A glimpse of builds, launches and showcases from RoboSpark labs.", "RoboSpark लैब के निर्माण, लॉन्च और प्रदर्शनियों की एक झलक।")} image={IMG.drone} testid="projects-hero" />
      <section className="bg-[#F0F4F8] text-[#0A192F] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {FILTERS.map((x) => (
              <button key={x} onClick={() => setF(x)} data-testid={`filter-${x.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${f === x ? "bg-[#FF3B3B] border-[#FF2E63] text-white" : "border-slate-300 text-slate-700 hover:border-slate-500 hover:border-white"}`}>{L(x, CATEGORY_HI[x])}</button>
            ))}
          </div>
          <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
            <AnimatePresence>
              {list.map((p, i) => (
                <motion.div layout key={p.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  onClick={p.student ? () => setOpen(p) : undefined}
                  className={`break-inside-avoid group relative rounded-2xl overflow-hidden border border-slate-200 bg-white ${p.student ? "cursor-pointer" : ""}`} data-testid={p.student ? `student-project-${p.id}` : `project-${p.id}`}>
                  {p.student && <span className="absolute top-3 left-3 z-10 bg-[#FF3B3B] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full inline-flex items-center gap-1"><Camera className="w-3 h-3" />{L("Student build", "छात्र निर्मित")} · {p.photos.length}</span>}
                  <img src={p.image} alt={p.title} className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i % 3 === 0 ? "h-80" : "h-60"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/75 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <span className="text-[10px] uppercase tracking-widest text-[#007A8A] font-bold">{L(p.category, CATEGORY_HI[p.category])}</span>
                    <h3 className="font-bold text-lg">{p.title}</h3>
                    <p className="text-xs text-slate-600">{p.school}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      <ProjectDialog item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
